import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { describeError } from "@/api/client";
import { useAuthStore } from "@/store/auth";

export function Login() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // Off by default: this screen is used in shared spaces.
  const [revealed, setRevealed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(username, password);
      navigate("/");
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <form onSubmit={submit} className="card p-6 w-full max-w-sm space-y-4">
        <div>
          <div className="label">Marketing</div>
          <h1 className="font-display font-bold text-[22px] tracking-tight">Command Center</h1>
        </div>

        <label className="block space-y-1">
          <span className="label">Username</span>
          <input
            value={username} onChange={(e) => setUsername(e.target.value)}
            autoComplete="username" required
            className="w-full text-sm bg-surface border border-rule rounded-md px-2.5 py-2
                       focus:outline-none focus:ring-2 focus:ring-petrol/30"
          />
        </label>

        <label className="block space-y-1">
          <span className="label">Password</span>
          <div className="relative">
            <input
              type={revealed ? "text" : "password"}
              value={password} onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password" required
              className="w-full text-sm bg-surface border border-rule rounded-md
                         pl-2.5 pr-10 py-2
                         focus:outline-none focus:ring-2 focus:ring-petrol/30"
            />
            {/* type=button, or it submits the form. Not inside the <label>'s
                click target either — tapping "show" must not focus the input
                and pop the keyboard on a phone. */}
            <button
              type="button"
              onClick={() => setRevealed((v) => !v)}
              aria-label={revealed ? "Hide password" : "Show password"}
              aria-pressed={revealed}
              title={revealed ? "Hide password" : "Show password"}
              className="absolute right-1 top-1/2 -translate-y-1/2 grid place-items-center
                         w-8 h-8 rounded text-ink-muted hover:text-ink
                         hover:bg-surface-alt focus-visible:outline
                         focus-visible:outline-2 focus-visible:outline-petrol"
            >
              {revealed ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </label>

        {error && (
          <p className="text-[13px] text-bad bg-bad-soft border border-bad/25 rounded-md px-3 py-2">
            {error}
          </p>
        )}

        <button type="submit" disabled={busy}
                className="btn btn-primary w-full justify-center disabled:opacity-60">
          {busy ? "Signing in…" : "Sign in"}
        </button>

        <p className="text-[12.5px] text-ink-muted text-center pt-1 border-t border-rule">
          No account?{" "}
          <Link to="/register" className="text-petrol hover:underline">
            Request access
          </Link>
          <span className="block text-[11.5px] text-ink-faint mt-0.5">
            You will need your employee ID. An administrator approves the request.
          </span>
        </p>
      </form>
    </div>
  );
}
